import {
  Button,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  notification,
} from "antd";
import { DeleteOutlined, EditOutlined, FireOutlined, PlusOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import axiosInstance from "../axios";
import { citysList } from "../states/citys";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Option } = Select;

const JOB_TYPE_OPTIONS = ["Full-Time", "Part-Time"];

const JobDeals = () => {
  const [citys] = useAtom(citysList);
  const [data, setData] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });

  const isHot = Form.useWatch("is_hot", form);
  const dealingsId = Form.useWatch("dealings_id", form);
  const templateId = Form.useWatch("template_id", form);

  const defaultTemplateId = templates.find((item) => item.is_default)?.id;

  const fetchTemplates = async () => {
    try {
      const response = await axiosInstance.get("/v1/cre/job-templates", {
        params: { page: 1, limit: 100, active_only: true },
      });
      setTemplates(response.data?.data || []);
    } catch (error) {
      notification.error({
        message: "Failed to load templates",
        description: "Could not fetch job templates.",
      });
    }
  };

  const fetchData = async (page, limit) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/v1/cre/job-deals", {
        params: { page, limit },
      });
      const rows = response.data?.data || [];
      setData(
        rows.map((item) => ({
          ...item,
          studentName: [item.student_first_name, item.student_last_name].filter(Boolean).join(" "),
          publishedAt: item.published_at
            ? dayjs(item.published_at).format("DD MMM YYYY, hh:mm A")
            : "",
        })),
      );
      setPagination({
        current: page,
        pageSize: limit,
        total: response.data?.meta?.total || 0,
      });
    } catch (error) {
      notification.error({
        message: "Failed to load jobs",
        description: "Could not fetch job deals list.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchData(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({
      job_type: "Part-Time",
      is_hot: false,
      template_id: defaultTemplateId,
    });
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue({
      dealings_id: record.dealings_id,
      title: record.title,
      city_id: record.city_id || undefined,
      location_display: record.location_display || "",
      salary_display: record.salary_display || "",
      job_type: record.job_type || "Part-Time",
      description: record.description || "",
      template_id: record.template_id || defaultTemplateId,
      is_hot: Boolean(record.is_hot),
      hot_message: record.hot_message || "",
    });
    setModalOpen(true);
  };

  const handleFillFromTemplate = async () => {
    const dealId = Number(dealingsId);
    if (!dealId) {
      notification.warning({
        message: "Deal ID required",
        description: "Enter a Linked Deal ID first to fill title and description from the template.",
      });
      return;
    }

    setPreviewLoading(true);
    try {
      const response = await axiosInstance.get("/v1/cre/job-deals/preview", {
        params: {
          dealings_id: dealId,
          template_id: templateId || undefined,
        },
      });

      if (!response.data?.success || !response.data?.data) {
        notification.error({
          message: "Could not apply template",
          description: response.data?.message || "Deal not found",
        });
        return;
      }

      const preview = response.data.data;
      form.setFieldsValue({
        title: preview.title,
        description: preview.description || "",
        city_id: preview.city_id || undefined,
        location_display: preview.location_display || "",
        salary_display: preview.salary_display || "",
        job_type: preview.job_type || "Part-Time",
        template_id: preview.template_id || templateId || defaultTemplateId,
      });
      notification.success({
        message: preview.template_name
          ? `Applied template: ${preview.template_name}`
          : "Title and description filled from template",
      });
    } catch (error) {
      notification.error({
        message: "Could not apply template",
        description: error.response?.data?.message || "Failed to load deal details",
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);

      const payload = {
        ...values,
        city_id: values.city_id || null,
        template_id: values.template_id || null,
        is_hot: Boolean(values.is_hot),
        hot_message: values.is_hot ? values.hot_message || undefined : null,
      };

      if (editingItem) {
        const response = await axiosInstance.put(`/v1/cre/job-deals/${editingItem.id}`, payload);
        if (response.data?.success) {
          notification.success({ message: response.data.message || "Job deal updated" });
        } else {
          notification.error({
            message: "Update failed",
            description: response.data?.message || "Unable to update job deal",
          });
          return;
        }
      } else {
        const response = await axiosInstance.post("/v1/cre/job-deals", payload);
        if (response.data?.success) {
          notification.success({ message: response.data.message || "Job deal created" });
        } else {
          notification.error({
            message: "Create failed",
            description: response.data?.message || "Unable to create job deal",
          });
          return;
        }
      }

      setModalOpen(false);
      fetchData(pagination.current, pagination.pageSize);
    } catch (error) {
      if (error.errorFields) return;
      notification.error({
        message: "Save failed",
        description: error.response?.data?.message || "Unable to save job deal",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleToggleHot = async (record, isHotValue) => {
    try {
      const response = await axiosInstance.post("/v1/cre/job-deals/toggle-hot", {
        id: record.id,
        is_hot: isHotValue,
      });

      if (response.data?.success) {
        setData((prev) =>
          prev.map((item) =>
            item.id === record.id ? { ...item, is_hot: isHotValue } : item,
          ),
        );
        notification.success({ message: response.data.message });
      } else {
        notification.error({
          message: "Update failed",
          description: response.data?.message || "Unable to update hot job status",
        });
      }
    } catch (error) {
      notification.error({
        message: "Update failed",
        description: "Unable to update hot job status",
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axiosInstance.delete(`/v1/cre/job-deals/${id}`);
      if (response.data?.success) {
        notification.success({ message: "Job deleted successfully" });
        fetchData(pagination.current, pagination.pageSize);
      } else {
        notification.error({
          message: "Delete failed",
          description: response.data?.message || "Unable to delete job",
        });
      }
    } catch (error) {
      notification.error({
        message: "Delete failed",
        description: "Unable to delete job",
      });
    }
  };

  const columns = [
    {
      title: "Deal ID",
      dataIndex: "dealings_id",
      key: "dealings_id",
      width: 100,
      render: (id) => (
        <a href={`/student/${id}`} target="_blank" rel="noopener noreferrer">
          {id}
        </a>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
    },
    {
      title: "Template",
      dataIndex: "template_name",
      key: "template_name",
      width: 180,
      render: (name) => name || "-",
    },
    {
      title: "Student",
      dataIndex: "studentName",
      key: "studentName",
      width: 160,
    },
    {
      title: "City",
      dataIndex: "city_name",
      key: "city_name",
      width: 120,
    },
    {
      title: "Salary",
      dataIndex: "salary_display",
      key: "salary_display",
      width: 120,
      render: (value) => value || "-",
    },
    {
      title: "Type",
      dataIndex: "job_type",
      key: "job_type",
      width: 100,
    },
    {
      title: "Deal Status",
      dataIndex: "deal_status",
      key: "deal_status",
      width: 120,
      render: (status) => (status ? <Tag>{status}</Tag> : "-"),
    },
    {
      title: "Hot Lead",
      dataIndex: "is_hot",
      key: "is_hot",
      width: 100,
      align: "center",
      render: (isHotValue, record) => (
        <Button
          color="danger"
          variant={isHotValue ? "filled" : "outlined"}
          size="small"
          icon={<FireOutlined />}
          onClick={() => handleToggleHot(record, !isHotValue)}
        >
          Hot
        </Button>
      ),
    },
    {
      title: "Published At",
      dataIndex: "publishedAt",
      key: "publishedAt",
      width: 170,
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this job from the portal?"
            description="This removes the job portal listing only. The original deal is not changed."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Flex vertical gap={12} style={{ overflow: "scroll", marginLeft: "5px", width: "100%" }}>
      <Flex justify="flex-end">
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Job Deal
        </Button>
      </Flex>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50, 100],
        }}
        loading={loading}
        onChange={(next) => fetchData(next.current, next.pageSize)}
        scroll={{ x: 1600 }}
      />

      <Modal
        title={editingItem ? "Edit Job Deal" : "Add Job Deal"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={submitLoading}
        okText={editingItem ? "Update" : "Create"}
        width={720}
        destroyOnClose
      >
        <p style={{ marginBottom: 16, color: "#666" }}>
          Select a template, then click <strong>Apply template</strong> to fill title and description
          from Class / Area / City / Subjects / Board. Publish from the student page uses the default template.
        </p>

        <Form form={form} layout="vertical">
          <Form.Item
            name="dealings_id"
            label="Linked Deal ID"
            rules={[{ required: true, message: "Deal ID is required" }]}
          >
            <Input
              type="number"
              placeholder="Enter dealings_id"
              disabled={Boolean(editingItem)}
            />
          </Form.Item>

          <Form.Item
            name="template_id"
            label="Job template"
            rules={[{ required: true, message: "Template is required" }]}
          >
            <Select
              showSearch
              placeholder="Select template"
              optionFilterProp="children"
            >
              {templates.map((template) => (
                <Option key={template.id} value={template.id}>
                  {template.name}
                  {template.is_default ? " (Default)" : ""}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ marginTop: -8 }}>
            <Button
              icon={<ThunderboltOutlined />}
              onClick={handleFillFromTemplate}
              loading={previewLoading}
              disabled={!dealingsId || !templateId}
            >
              Apply template
            </Button>
          </Form.Item>

          <Form.Item
            name="title"
            label="Job Title"
            rules={[{ required: true, message: "Title is required" }]}
          >
            <Input placeholder="Apply a template or enter title manually" />
          </Form.Item>

          <Form.Item name="city_id" label="City">
            <Select
              allowClear
              showSearch
              placeholder="Select city"
              optionFilterProp="children"
            >
              {Object.entries(citys).map(([cityId, cityName]) => (
                <Option key={cityId} value={Number(cityId)}>
                  {cityName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="location_display" label="Location display (portal)">
            <Input placeholder="Area, City shown on jobs portal" />
          </Form.Item>

          <Form.Item name="salary_display" label="Salary display">
            <Input placeholder="e.g. ₹8000/month" />
          </Form.Item>

          <Form.Item
            name="job_type"
            label="Job Type"
            rules={[{ required: true, message: "Job type is required" }]}
          >
            <Select>
              {JOB_TYPE_OPTIONS.map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={6} placeholder="Apply a template or enter description manually" />
          </Form.Item>

          <Form.Item name="is_hot" label="Hot Job" valuePropName="checked">
            <Switch />
          </Form.Item>

          {isHot && (
            <Form.Item name="hot_message" label="Hot job message">
              <Input placeholder="This is a high-priority tuition opportunity !" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Flex>
  );
};

export default JobDeals;
